/**
 * `next/link` shim.
 *
 * The components under `src/components/` are plain `"use client"` React and
 * move to the SPA untouched (FE-03) — except that they import a Next.js
 * `<Link href=…>`. Rather than rewrite every call site to react-router's
 * `<Link to=…>`, this module presents react-router's Link under Next's API,
 * so porting a component is a ONE-LINE import change:
 *
 *   -import Link from "next/link";
 *   +import Link from "@/compat/link";
 *
 * Next-only props (`prefetch`, `scroll`, `shallow`, `locale`, `passHref`,
 * `legacyBehavior`) are accepted and dropped — react-router has no equivalent
 * and none of them changes what the user sees.
 */
import { forwardRef } from "react";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router-dom";

type AnchorProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

export interface LinkProps extends AnchorProps {
  href: string;
  replace?: boolean;
  scroll?: boolean;
  prefetch?: boolean | null;
  shallow?: boolean;
  passHref?: boolean;
  legacyBehavior?: boolean;
  locale?: string | false;
}

/** Absolute URLs, protocol-relative URLs and non-http schemes stay plain <a>. */
function isExternal(href: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(href) || href.startsWith("//");
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, replace, scroll, prefetch, shallow, passHref, legacyBehavior, locale, ...rest },
  ref,
) {
  void scroll;
  void prefetch;
  void shallow;
  void passHref;
  void legacyBehavior;
  void locale;

  if (isExternal(href) || href.startsWith("#")) {
    return <a ref={ref} href={href} {...rest} />;
  }

  const props: RouterLinkProps = { to: href, replace, ...rest };
  return <RouterLink ref={ref} {...props} />;
});

export default Link;
